/* =====================================================================
 * authVault — 계정별 인증 저장소
 *
 * - runtime memory: 계정별 access token
 * - storage: 탭/포커스/동기화 메타데이터만 저장
 *
 * All storage keys are tenant-scoped via `activeTenantKey(...)` so B2C
 * (`askyourai_*`), Samsung (`samsung_*`), and Sungwoo (`sungwoo_*`) never
 * collide on the same browser profile — critical on dev machines and any
 * reverse-proxy deployment that serves multiple tenants from one origin.
 * Pre-launch we intentionally do not migrate legacy `auw_*` keys.
 * ===================================================================== */

import { activeTenantKey } from '@auw/tenant';

export interface AuthAccountRecord {
  accountId: string;
  userId: string;
  accessToken: string;
  updatedAt: string;
}

interface AuthAccountsState {
  accounts: Record<string, AuthAccountRecord>;
}

/* Tenant-scoped storage keys, resolved lazily on first read.
 *
 * Vite/Rollup chunk-split heuristics may co-locate this module with
 * a chunk evaluated alongside the entry, before the host has called
 * `setActiveTenantManifest()`. Reading the manifest at module-eval
 * time (`const X = activeTenantKey(...)`) used to throw with
 * "Active tenant manifest has not been set" under those conditions
 * (2026-04-29 dev.askyour.ai blank-screen regression). Lazy
 * memoised getters defer the read to first auth-vault call site,
 * which always runs after host bootstrap. */
let _activeAccountStorageKey: string | null = null;
function getActiveAccountStorageKey(): string {
  if (_activeAccountStorageKey === null) _activeAccountStorageKey = activeTenantKey('active_account_id');
  return _activeAccountStorageKey;
}
let _lastUsedAccountStorageKey: string | null = null;
function getLastUsedAccountStorageKey(): string {
  if (_lastUsedAccountStorageKey === null) _lastUsedAccountStorageKey = activeTenantKey('last_used_account_id');
  return _lastUsedAccountStorageKey;
}
let _focusedAccountStorageKey: string | null = null;
function getFocusedAccountStorageKey(): string {
  if (_focusedAccountStorageKey === null) _focusedAccountStorageKey = activeTenantKey('focused_account');
  return _focusedAccountStorageKey;
}
let _tabIdStorageKey: string | null = null;
function getTabIdStorageKey(): string {
  if (_tabIdStorageKey === null) _tabIdStorageKey = activeTenantKey('auth_tab_id');
  return _tabIdStorageKey;
}
let _autoRestoreSuppressKey: string | null = null;
function getAutoRestoreSuppressKey(): string {
  if (_autoRestoreSuppressKey === null) _autoRestoreSuppressKey = activeTenantKey('auth_suppress_auto_restore');
  return _autoRestoreSuppressKey;
}
let _authSignalStorageKey: string | null = null;
function getAuthSignalStorageKey(): string {
  if (_authSignalStorageKey === null) _authSignalStorageKey = activeTenantKey('auth_signal');
  return _authSignalStorageKey;
}
const FOCUSED_ACCOUNT_MAX_AGE_MS = 15 * 60 * 1000;

const EMPTY_STATE: AuthAccountsState = { accounts: {} };
const runtimeAccounts = new Map<string, AuthAccountRecord>();

interface FocusedAccountState {
  tabId: string;
  accountId: string;
  focusedAt: string;
}

interface AuthSignalState {
  type: 'account_removed' | 'clear_all';
  accountId?: string;
  emittedAt: string;
}

function canUseWindowStorage(): boolean {
  return typeof window !== 'undefined'
    && typeof window.localStorage !== 'undefined'
    && typeof window.sessionStorage !== 'undefined';
}

function normalizeAccountId(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed ? trimmed : null;
}

function readAccountsState(): AuthAccountsState {
  if (runtimeAccounts.size === 0) return EMPTY_STATE;
  const normalized: Record<string, AuthAccountRecord> = {};
  for (const [accountId, account] of runtimeAccounts.entries()) {
    normalized[accountId] = { ...account };
  }
  return { accounts: normalized };
}

function writeAccountsState(next: AuthAccountsState): void {
  runtimeAccounts.clear();
  for (const [key, value] of Object.entries(next.accounts || {})) {
    if (!value || typeof value !== 'object') continue;
    const accountId = normalizeAccountId(value.accountId || key);
    const userId = normalizeAccountId(value.userId);
    const accessToken = typeof value.accessToken === 'string' ? value.accessToken : '';
    const updatedAt = typeof value.updatedAt === 'string' ? value.updatedAt : new Date().toISOString();
    if (!accountId || !userId || !accessToken) continue;
    runtimeAccounts.set(accountId, {
      accountId,
      userId,
      accessToken,
      updatedAt,
    });
  }
}

function dropRuntimeAccount(accountId: string): void {
  const normalized = normalizeAccountId(accountId);
  if (!normalized) return;
  runtimeAccounts.delete(normalized);
}

function parseAuthSignal(raw: string | null): AuthSignalState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<AuthSignalState>;
    const type = parsed.type;
    if (type !== 'account_removed' && type !== 'clear_all') return null;
    const accountId = normalizeAccountId(parsed.accountId);
    const emittedAt = typeof parsed.emittedAt === 'string' ? parsed.emittedAt : '';
    if (!emittedAt) return null;
    return {
      type,
      accountId: accountId || undefined,
      emittedAt,
    };
  } catch {
    return null;
  }
}

function emitAuthSignal(payload: Omit<AuthSignalState, 'emittedAt'>): void {
  if (!canUseWindowStorage()) return;
  try {
    window.localStorage.setItem(
      getAuthSignalStorageKey(),
      JSON.stringify({
        ...payload,
        emittedAt: new Date().toISOString(),
      } satisfies AuthSignalState),
    );
  } catch {
    // no-op
  }
}

function createTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getCurrentTabId(): string | null {
  if (!canUseWindowStorage()) return null;
  return normalizeAccountId(window.sessionStorage.getItem(getTabIdStorageKey()));
}

function getOrCreateTabId(): string {
  const current = getCurrentTabId();
  if (current) return current;
  const created = createTabId();
  if (canUseWindowStorage()) {
    window.sessionStorage.setItem(getTabIdStorageKey(), created);
  }
  return created;
}

export function getAuthTabId(): string {
  return getOrCreateTabId();
}

function getLastUsedAccountId(): string | null {
  if (!canUseWindowStorage()) return null;
  return normalizeAccountId(window.localStorage.getItem(getLastUsedAccountStorageKey()));
}

function setLastUsedAccountId(accountId: string | null): void {
  if (!canUseWindowStorage()) return;
  const normalized = normalizeAccountId(accountId);
  if (!normalized) {
    window.localStorage.removeItem(getLastUsedAccountStorageKey());
    return;
  }
  window.localStorage.setItem(getLastUsedAccountStorageKey(), normalized);
}

function readFocusedAccountState(): FocusedAccountState | null {
  if (!canUseWindowStorage()) return null;
  try {
    const raw = window.localStorage.getItem(getFocusedAccountStorageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FocusedAccountState>;
    const tabId = normalizeAccountId(parsed.tabId);
    const accountId = normalizeAccountId(parsed.accountId);
    const focusedAt = typeof parsed.focusedAt === 'string' ? parsed.focusedAt : '';
    if (!tabId || !accountId || !focusedAt) return null;
    return { tabId, accountId, focusedAt };
  } catch {
    return null;
  }
}

function getFreshFocusedAccountId(): string | null {
  const focused = readFocusedAccountState();
  if (!focused) return null;
  const ts = Date.parse(focused.focusedAt);
  const isFresh = Number.isFinite(ts) && (Date.now() - ts <= FOCUSED_ACCOUNT_MAX_AGE_MS);
  if (!isFresh) return null;
  return focused.accountId;
}

function writeFocusedAccountState(accountId: string): void {
  if (!canUseWindowStorage()) return;
  const normalized = normalizeAccountId(accountId);
  if (!normalized) return;
  const payload: FocusedAccountState = {
    tabId: getOrCreateTabId(),
    accountId: normalized,
    focusedAt: new Date().toISOString(),
  };
  window.localStorage.setItem(getFocusedAccountStorageKey(), JSON.stringify(payload));
}

function clearFocusedAccountState(): void {
  if (!canUseWindowStorage()) return;
  window.localStorage.removeItem(getFocusedAccountStorageKey());
}

function clearFocusedAccountIfMatches(accountId: string): void {
  const focused = readFocusedAccountState();
  if (focused?.accountId !== accountId) return;
  clearFocusedAccountState();
}

export function suppressAutoRestoreForTab(): void {
  if (!canUseWindowStorage()) return;
  window.sessionStorage.setItem(getAutoRestoreSuppressKey(), '1');
}

export function clearAutoRestoreSuppressionForTab(): void {
  if (!canUseWindowStorage()) return;
  window.sessionStorage.removeItem(getAutoRestoreSuppressKey());
}

export function isAutoRestoreSuppressedForTab(): boolean {
  if (!canUseWindowStorage()) return false;
  return window.sessionStorage.getItem(getAutoRestoreSuppressKey()) === '1';
}

export function getActiveAccountId(): string | null {
  if (!canUseWindowStorage()) return null;
  return normalizeAccountId(window.sessionStorage.getItem(getActiveAccountStorageKey()));
}

/**
 * access token이 메모리에 없거나 active account가 아직 복원되지 않은 탭에서도
 * refresh 잠금 키를 일관되게 잡기 위한 best-effort account id입니다.
 *
 * 현재 브라우저는 단일 계정 정책이므로 active → 최근 포커스 계정 → 마지막 사용 계정
 * 순서로 힌트를 선택합니다.
 */
export function getRefreshCoordinationAccountId(): string | null {
  return getActiveAccountId() || getFreshFocusedAccountId() || getLastUsedAccountId();
}

export function setActiveAccountId(accountId: string | null): void {
  if (!canUseWindowStorage()) return;
  const normalized = normalizeAccountId(accountId);
  if (!normalized) {
    window.sessionStorage.removeItem(getActiveAccountStorageKey());
    const focused = readFocusedAccountState();
    const tabId = getCurrentTabId();
    if (focused && tabId && focused.tabId === tabId) {
      clearFocusedAccountState();
    }
    return;
  }
  clearAutoRestoreSuppressionForTab();
  window.sessionStorage.setItem(getActiveAccountStorageKey(), normalized);
  setLastUsedAccountId(normalized);
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    writeFocusedAccountState(normalized);
  }
}

export function bootstrapActiveAccountId(options?: {
  excludeAccountIds?: string[];
}): string | null {
  if (!canUseWindowStorage()) return null;
  if (isAutoRestoreSuppressedForTab()) return null;

  const excluded = new Set(
    (options?.excludeAccountIds || [])
      .map((id) => normalizeAccountId(id))
      .filter((id): id is string => !!id),
  );
  const state = readAccountsState();
  const accounts = state.accounts;
  const activeId = getActiveAccountId();

  if (activeId && accounts[activeId] && !excluded.has(activeId)) {
    setLastUsedAccountId(activeId);
    return activeId;
  }
  if (activeId && (!accounts[activeId] || excluded.has(activeId))) {
    window.sessionStorage.removeItem(getActiveAccountStorageKey());
  }

  const focused = readFocusedAccountState();
  if (focused) {
    const ts = Date.parse(focused.focusedAt);
    const isFresh = Number.isFinite(ts) && (Date.now() - ts <= FOCUSED_ACCOUNT_MAX_AGE_MS);
    const focusedAccount = accounts[focused.accountId];
    if (isFresh && focusedAccount && !excluded.has(focused.accountId)) {
      setActiveAccountId(focused.accountId);
      return focused.accountId;
    }
    if (!isFresh || !focusedAccount) {
      clearFocusedAccountState();
    }
  }

  const lastUsed = getLastUsedAccountId();
  if (lastUsed && accounts[lastUsed] && !excluded.has(lastUsed)) {
    setActiveAccountId(lastUsed);
    return lastUsed;
  }

  const fallback = Object.values(accounts)
    .filter((account) => !excluded.has(account.accountId))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  if (!fallback) return null;

  setActiveAccountId(fallback.accountId);
  return fallback.accountId;
}

export function syncFocusedAccountFromActive(): void {
  if (!canUseWindowStorage()) return;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  const activeId = getActiveAccountId();
  if (!activeId) return;
  writeFocusedAccountState(activeId);
}

export function startFocusedAccountTracking(): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  const sync = () => {
    syncFocusedAccountFromActive();
  };
  const syncWithFollowUp = () => {
    sync();
    // 탭 전환 직후 새 탭이 아주 빠르게 열리는 경우를 대비한 보강 sync
    window.setTimeout(sync, 0);
    window.setTimeout(sync, 120);
  };
  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    syncWithFollowUp();
  };

  window.addEventListener('focus', syncWithFollowUp);
  window.addEventListener('pageshow', syncWithFollowUp);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  syncWithFollowUp();

  return () => {
    window.removeEventListener('focus', syncWithFollowUp);
    window.removeEventListener('pageshow', syncWithFollowUp);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

/**
 * 다른 탭에서 현재 active 계정이 제거되면(로그아웃/탈퇴),
 * 이 탭도 자동으로 동일 계정을 로그아웃 상태로 맞춥니다.
 */
export function startActiveAccountInvalidationGuard(onInvalidated: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.storageArea !== window.localStorage) return;
    if (event.key !== getAuthSignalStorageKey() || !event.newValue) return;

    const signal = parseAuthSignal(event.newValue);
    if (!signal) return;

    const activeId = getActiveAccountId();
    if (!activeId) return;

    const shouldInvalidate = (
      signal.type === 'clear_all'
      || !signal.accountId
      || signal.accountId === activeId
    );
    if (!shouldInvalidate) return;

    dropRuntimeAccount(activeId);
    suppressAutoRestoreForTab();
    setActiveAccountId(null);
    onInvalidated();
  };

  window.addEventListener('storage', handleStorage);
  return () => {
    window.removeEventListener('storage', handleStorage);
  };
}

export function listAuthAccounts(): AuthAccountRecord[] {
  const state = readAccountsState();
  return Object.values(state.accounts).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getAuthAccount(accountId: string): AuthAccountRecord | null {
  const normalized = normalizeAccountId(accountId);
  if (!normalized) return null;
  const state = readAccountsState();
  return state.accounts[normalized] ?? null;
}

export function getActiveAuthAccount(): AuthAccountRecord | null {
  const activeId = getActiveAccountId();
  if (!activeId) return null;
  const state = readAccountsState();
  const account = state.accounts[activeId] ?? null;
  if (!account) {
    setActiveAccountId(null);
  }
  return account;
}

export function upsertAuthAccount(params: {
  accountId: string;
  userId?: string | null;
  accessToken: string;
}): AuthAccountRecord {
  const accountId = normalizeAccountId(params.accountId);
  if (!accountId) {
    throw new Error('accountId가 필요합니다.');
  }
  const accessToken = (params.accessToken || '').trim();
  if (!accessToken) {
    throw new Error('accessToken이 비어 있습니다.');
  }

  const state = readAccountsState();
  const prev = state.accounts[accountId];
  const userId = normalizeAccountId(params.userId || prev?.userId || accountId);
  if (!userId) {
    throw new Error('userId를 확인할 수 없습니다.');
  }

  const next: AuthAccountRecord = {
    accountId,
    userId,
    accessToken,
    updatedAt: new Date().toISOString(),
  };

  const merged: AuthAccountsState = {
    accounts: {
      // 정책 A: 동일 브라우저 프로필에서 단일 계정만 유지
      [accountId]: next,
    },
  };
  writeAccountsState(merged);
  return next;
}

export function removeAuthAccount(accountId: string): void {
  const normalized = normalizeAccountId(accountId);
  if (!normalized) return;

  const state = readAccountsState();
  if (!state.accounts[normalized]) return;
  const nextAccounts = { ...state.accounts };
  delete nextAccounts[normalized];
  writeAccountsState({ accounts: nextAccounts });
  if (getLastUsedAccountId() === normalized) {
    setLastUsedAccountId(null);
  }
  clearFocusedAccountIfMatches(normalized);

  if (getActiveAccountId() === normalized) {
    setActiveAccountId(null);
  }
  emitAuthSignal({ type: 'account_removed', accountId: normalized });
}

export function removeActiveAuthAccount(): void {
  const activeId = getActiveAccountId();
  if (!activeId) return;
  removeAuthAccount(activeId);
}

export function clearAllAuthAccounts(): void {
  runtimeAccounts.clear();
  if (canUseWindowStorage()) {
    window.localStorage.removeItem(getLastUsedAccountStorageKey());
    window.localStorage.removeItem(getFocusedAccountStorageKey());
    window.sessionStorage.removeItem(getActiveAccountStorageKey());
    window.sessionStorage.removeItem(getTabIdStorageKey());
    window.sessionStorage.removeItem(getAutoRestoreSuppressKey());
  }
  emitAuthSignal({ type: 'clear_all' });
}
